import React, { useState, useRef } from 'react';
import TendModal from './TendModal';

interface UploadLogoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, displayName: string, logoKey: string) => Promise<void>;
  uploadingIconType?: 'icon_white' | null;
}

const UploadLogoModal: React.FC<UploadLogoModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  uploadingIconType = null
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [logoKey, setLogoKey] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pre-fill fields if uploading an icon
  const isUploadingIcon = uploadingIconType !== null;
  const iconDisplayName = 'Brand Icon';

  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setError(null);

    if (!selectedFile) {
      return;
    }

    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setError('Invalid file type. Please upload a PNG, JPEG, or SVG image.');
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File size too large. Maximum size is 5MB.');
      return;
    }

    setFile(selectedFile);

    // Generate preview and get image dimensions
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageUrl = reader.result as string;
      setPreviewUrl(imageUrl);

      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = imageUrl;
    };
    reader.readAsDataURL(selectedFile);

    // Auto-generate logo key from filename
    const fileName = selectedFile.name.split('.')[0];
    const cleanKey = fileName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (!logoKey) {
      setLogoKey(cleanKey);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    const finalDisplayName = isUploadingIcon ? iconDisplayName : displayName;
    const finalLogoKey = isUploadingIcon ? uploadingIconType! : logoKey;

    if (!finalDisplayName.trim()) {
      setError('Please enter a display name for the logo.');
      return;
    }

    if (!finalLogoKey.trim()) {
      setError('Please enter a logo key (identifier).');
      return;
    }

    if (!/^[a-z0-9_]+$/.test(finalLogoKey)) {
      setError('Logo key must contain only lowercase letters, numbers, and underscores.');
      return;
    }

    try {
      setUploading(true);
      await onUpload(file, finalDisplayName, finalLogoKey);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setDisplayName('');
    setLogoKey('');
    setPreviewUrl(null);
    setImageDimensions(null);
    setError(null);
    setUploading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <TendModal
      title={isUploadingIcon ? 'Upload Brand Icon' : 'Upload Logo Variant'}
      onClose={handleClose}
      width={460}
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* File Input */}
        <div>
          <span className="tend-micro" style={{ display: 'block', marginBottom: 4 }}>Logo file</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.svg"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: '100%',
              padding: '14px 12px',
              borderRadius: 12,
              border: '1px dashed rgba(255,255,255,.35)',
              background: 'none',
              color: file ? 'var(--text)' : 'var(--text-3)',
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: 13,
              cursor: 'pointer',
              transition: 'border-color .2s, color .2s',
            }}
          >
            {file ? file.name : 'Click to select image (PNG, JPEG, SVG)'}
          </button>
          <span className="tend-micro" style={{ display: 'block', marginTop: 4, textTransform: 'none', letterSpacing: 0.4 }}>
            Maximum file size: 5MB
          </span>
        </div>

        {/* Preview */}
        {previewUrl && (
          <div
            style={{
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,.10)',
              background: 'rgba(255,255,255,.05)',
              padding: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span className="tend-micro">Preview</span>
              {imageDimensions && (
                <span className="tend-micro" style={{ textTransform: 'none', letterSpacing: 0.4 }}>
                  {imageDimensions.width}×{imageDimensions.height}px
                </span>
              )}
            </div>
            <img
              src={previewUrl}
              alt="Logo preview"
              style={{ maxHeight: 128, margin: '0 auto', display: 'block', objectFit: 'contain' }}
            />
          </div>
        )}

        {/* Icon Info (if uploading icon) */}
        {isUploadingIcon && (
          <div
            style={{
              borderRadius: 12,
              border: '1px solid rgba(178,140,255,.35)',
              background: 'rgba(178,140,255,.10)',
              padding: 12,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--signal-scribe)' }}>
              Uploading Brand Icon
            </span>
            <span className="tend-micro" style={{ display: 'block', marginTop: 3, textTransform: 'none', letterSpacing: 0.4 }}>
              This icon will be displayed alongside the brand name.
            </span>
          </div>
        )}

        {/* Display Name (only for custom logos) */}
        {!isUploadingIcon && (
          <div>
            <span className="tend-micro" style={{ display: 'block', marginBottom: 4 }}>Display name</span>
            <input
              type="text"
              className="tend-ink-field"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Holiday Special Logo"
            />
          </div>
        )}

        {/* Logo Key (only for custom logos) */}
        {!isUploadingIcon && (
          <div>
            <span className="tend-micro" style={{ display: 'block', marginBottom: 4 }}>Logo key (identifier)</span>
            <input
              type="text"
              className="tend-ink-field"
              value={logoKey}
              onChange={(e) => setLogoKey(e.target.value.toLowerCase())}
              placeholder="e.g., holiday_2024"
            />
            <span className="tend-micro" style={{ display: 'block', marginTop: 4, textTransform: 'none', letterSpacing: 0.4 }}>
              Lowercase letters, numbers, and underscores only
            </span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <span className="tend-micro" style={{ color: 'var(--signal-rec-soft)', textTransform: 'none', letterSpacing: 0.4 }}>
            {error}
          </span>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
          <button
            type="button"
            className="tend-btn tend-btn--glass"
            style={{ padding: '8px 16px', fontSize: 13 }}
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="tend-btn tend-btn--commit"
            style={{ padding: '8px 16px', fontSize: 13 }}
            disabled={uploading || !file}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
    </TendModal>
  );
};

export default UploadLogoModal;
